import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AssessmentResult } from '../models/assessment-result.model';

@Injectable({
  providedIn: 'root',
})
export class AssessmentResultService {
  private resultsCache = new Map<string, AssessmentResult>();
  private resultsSubject = new BehaviorSubject<AssessmentResult[]>([]);

  results$ = this.resultsSubject.asObservable();

  constructor() {
    this.loadAllResults();
  }

  /**
   * Lädt alle verfügbaren Assessment-Ergebnisse
   */
  loadAllResults(): void {
    // Hier würde in einer vollständigen Implementierung eine API abgefragt werden
    // Für dieses Beispiel laden wir Daten aus dem localStorage
    try {
      const savedResults = localStorage.getItem('assessmentResults');
      if (savedResults) {
        const results = JSON.parse(savedResults) as AssessmentResult[];
        results.forEach(result => {
          // Konvertieren von String-Daten zu Date-Objekten
          result.date = new Date(result.date);
          if (result.patientData?.birthDate) {
            result.patientData.birthDate = new Date(result.patientData.birthDate);
          }
          this.resultsCache.set(result.id, result);
        });
        this.resultsSubject.next(results);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Assessment-Ergebnisse:', error);
    }
  }

  /**
   * Speichert ein Assessment-Ergebnis
   */
  saveResult(result: AssessmentResult): Observable<AssessmentResult> {
    // In einer vollständigen Implementierung würde hier eine API aufgerufen werden
    // Für dieses Beispiel speichern wir im localStorage
    try {
      // Sicherstellen, dass die ID eindeutig ist
      if (!result.id) {
        result.id = `result-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      this.resultsCache.set(result.id, result);
      const currentResults = this.resultsSubject.value;
      const updatedResults = currentResults.filter(r => r.id !== result.id).concat(result);
      this.resultsSubject.next(updatedResults);

      // Im localStorage speichern
      localStorage.setItem('assessmentResults', JSON.stringify(updatedResults));

      return of(result);
    } catch (error) {
      console.error('Fehler beim Speichern des Assessment-Ergebnisses:', error);
      return of(result);
    }
  }

  /**
   * Lädt ein Assessment-Ergebnis anhand der ID
   */
  getResultById(id: string): Observable<AssessmentResult | undefined> {
    if (this.resultsCache.has(id)) {
      return of(this.resultsCache.get(id));
    }

    // Wenn nicht im Cache, versuchen zu laden (in einer vollständigen Implementierung)
    return of(undefined);
  }

  /**
   * Lädt alle Assessment-Ergebnisse für einen Patienten
   */
  getResultsByPatientId(patientId: string): Observable<AssessmentResult[]> {
    const patientResults = this.resultsSubject.value.filter(r => r.patientId === patientId);
    return of(patientResults);
  }

  /**
   * Löscht ein Assessment-Ergebnis
   */
  deleteResult(id: string): Observable<boolean> {
    try {
      this.resultsCache.delete(id);
      const currentResults = this.resultsSubject.value;
      const updatedResults = currentResults.filter(r => r.id !== id);
      this.resultsSubject.next(updatedResults);

      // Im localStorage aktualisieren
      localStorage.setItem('assessmentResults', JSON.stringify(updatedResults));

      return of(true);
    } catch (error) {
      console.error('Fehler beim Löschen des Assessment-Ergebnisses:', error);
      return of(false);
    }
  }
}
